import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

interface InfoRowProps {
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly label: string;
  readonly value: string | number | ReactNode | null | undefined;
  readonly valueClassName?: string;
  readonly labelClassName?: string;
  readonly iconClassName?: string;
  /** Disable copy button even for copyable values */
  readonly disableCopy?: boolean;
  /** Allow value text to wrap instead of holding rigid width (for long text) */
  readonly wrapValue?: boolean;
}

export const InfoRow = ({
  icon: Icon,
  label,
  value,
  valueClassName,
  labelClassName,
  iconClassName,
  disableCopy = false,
  wrapValue = false,
}: InfoRowProps) => {
  const [copied, setCopied] = useState(false);

  // Don't render if no value
  if (value === null || value === undefined) return null;

  // Determine if value is copyable (string or number, not ReactNode)
  const isCopyable =
    !disableCopy && (typeof value === 'string' || typeof value === 'number');
  const copyValue = isCopyable ? String(value) : '';

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isCopyable) return;

    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail - clipboard API may not be available
    }
  };

  return (
    <div className="group flex min-h-[28px] items-center justify-between gap-3 py-1">
      <div
        className={cn(
          'flex items-center gap-2',
          wrapValue ? 'shrink-0' : 'min-w-0',
        )}
      >
        <Icon
          className={cn('h-3.5 w-3.5 shrink-0 text-gray-400', iconClassName)}
        />
        <span
          className={cn(
            'text-[11px] font-medium text-gray-500',
            !wrapValue && 'truncate',
            labelClassName,
          )}
        >
          {label}
        </span>
      </div>
      <div
        className={cn(
          'flex items-center gap-1.5 pl-2',
          wrapValue ? 'min-w-0' : 'shrink-0',
        )}
      >
        <div
          className={cn(
            'text-right text-[11px] font-medium text-gray-900',
            valueClassName,
          )}
        >
          {value}
        </div>
        {isCopyable && (
          <button
            type="button"
            onClick={handleCopy}
            className={cn(
              'rounded p-0.5 opacity-0 transition-all group-hover:opacity-100',
              'hover:bg-gray-100 active:bg-gray-200',
              copied && 'bg-emerald-50 text-emerald-600 opacity-100',
            )}
            title={copied ? 'Copied!' : 'Copy to clipboard'}
          >
            {copied ? (
              <CheckIcon className="h-3 w-3" />
            ) : (
              <ClipboardDocumentIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};
